import { CircularProgress, Grid, Stack, Typography } from '@mui/material';

import { useEffect, useState, useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useNavigate, useParams } from 'react-router-dom';

import AccordionComp from 'common/components/Accordion/AccordionComp';
import ButtonComp from 'common/components/Button/ButtonComp';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { MODULE_NAME, TABLE_CONSTANTS, TASKTYPE } from 'common/constants';
import {
	PROGRESS_BAR_ELEMENTS,
	STATUS_UPDATE_TYPE,
} from 'common/constants/CitationDetails';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { setIsOpenInvententoryCommentDrawer } from 'common/store/reducer/regulationInventoryReducer';
import { urlWithTenant } from 'common/utils/utilFunctions';

import useTableQuery from 'hooks/useTableQuery';

import CitationDescription from 'features/RegulationInventory/Components/CitationDescription/CitationDescription';
import RegulationBreadcrumbs from 'features/RegulationInventory/Components/RegulationBreadcrumb/RegulationBreadcrumbs';
import RegulationDetailsHeader from 'features/RegulationInventory/Components/RegulationHeader/RegulationDetailsHeader';

import { getGroupCitationDetails, putGroupAssessment } from '../api';
import {
	ICitationDetailsElements,
	IGroupAssessmentChangeParams,
	IGroupAssessmentUpdateRequest,
	IGroupCitationDetails,
} from '../api/type-def';

import {
	getAllChildrenNodes,
	getGroupingBreadCrumbs,
} from './RegulationGroupUtil';

import styles from './GroupReviewDetails.module.scss';

const { REQUIREMENT_INVENTORY_NAME } = MODULE_NAME;

const { OK, CREATED } = HTTP_STATUS_CODES;

const { WORKFLOW } = TASKTYPE;

const { GROUPING_ASSESSMENT, GROUPING_REVIEW, SUMMARY_ASSESSMENT } =
	PROGRESS_BAR_ELEMENTS;

const { APPROVE, REJECT } = STATUS_UPDATE_TYPE;

const { customPageSize } = TABLE_CONSTANTS;

const { regulationInventory, overView } = rrcpDashboardRoutes;

interface IGroupReviewProps {
	storageWrapper: any;
}
const GroupReviewDetails = (props: IGroupReviewProps) => {
	const { storageWrapper } = props;

	const [group, setGroup] = useState<IGroupCitationDetails>(
		{} as IGroupCitationDetails,
	);
	const [selectedAccordians, setSelectedAccordians] = useState<
		ICitationDetailsElements[]
	>([]);
	const [loading, setIsLoading] = useState<boolean>(false);
	const commentDrawer = useSelector(
		(state: RootState) =>
			state.regulationInventory.isOpenInvententoryCommentDrawer,
	);
	const isCommentAdded = useSelector(
		(state: RootState) => state.regulationInventory.isCommentAdded,
	);
	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const isAssignedUserSameAsLoggedInUser =
		loggedInUser?.userId === group?.assignedUserId;

	const { groupId } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [isRejected, setIsRejected] = useState<boolean>(false);

	useEffect(() => {
		fetchGroupDetails();
	}, []);

	const fetchGroupDetails = async () => {
		setIsLoading(true);
		const resp: any = await getGroupCitationDetails(groupId || '');
		if (resp?.statusCode === OK) {
			setIsLoading(false);
			setGroup(resp?.responseBody);
		} else {
			setIsLoading(false);
		}
	};

	const handleAccordianChange = (
		val: ICitationDetailsElements,
		event: React.SyntheticEvent,
	) => {
		event.stopPropagation();
		if (selectedAccordians.includes(val)) {
			setSelectedAccordians((res) => res.filter((elem) => elem !== val));
		} else {
			setSelectedAccordians([...selectedAccordians, val]);
		}
	};

	const onGroupReviewReject = () => {
		dispatch(setIsOpenInvententoryCommentDrawer(true));
		setIsRejected(true);
	};

	const { updateTableQuery: updateGroupingObjRegTableQuery } = useTableQuery({
		id: 'GroupingObRegTable',
	});

	const onGroupReviewApproval = () => {
		updateTaskStatus(SUMMARY_ASSESSMENT, APPROVE);

		const defaultQuery = {
			pageNumber: 0,
			pageSize: customPageSize,
		};
		updateGroupingObjRegTableQuery(defaultQuery);
	};

	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);

	useEffect(() => {
		if (group && isRejected && isCommentAdded && !commentDrawer) {
			updateTaskStatus(GROUPING_ASSESSMENT, REJECT);
			setIsRejected(false);
		}
	}, [group, isRejected, isCommentAdded, commentDrawer]);

	const updateTaskStatus = async (status: string, actionType: string) => {
		const selectedNodes: IGroupAssessmentChangeParams[] = [];
		group.grouping.forEach((node) => {
			const nodes = getAllChildrenNodes(node);
			nodes.forEach((childNode: string) => {
				const checkedObj: IGroupAssessmentChangeParams = {
					citationId: childNode,
					regulationRelationshipId: node.id,
					groups: [group.groupId],
				};
				selectedNodes.push(checkedObj);
			});
		});

		const putArgument: IGroupAssessmentUpdateRequest = {
			taskDetails: {
				taskId: group.taskId,
				taskStatus: status,
				regulationRelationshipId: group.sectionId,
				sectionId: group.sectionId,
				sectionName: group.sectionName,
				assessmentUpdate: false,
				taskType: WORKFLOW,
				moduleName: REQUIREMENT_INVENTORY_NAME,
				actionType,
			},
			grouping: selectedNodes,
		};
		const response = await putGroupAssessment(putArgument);
		if (response?.statusCode === OK || response?.statusCode === CREATED) {
			dispatch(
				setToastMessage({
					message: 'The details saved successfully',
					severity: 'success',
					isToastOpen: true,
				}),
			);
			navigate(urlWithTenant(`/${regulationInventory}/${overView}`));
		}
		if (response?.errorBody || response?.error) {
			const { errorBody } = response;
			const responseError: any = response.error;
			const errorMsg =
				(errorBody?.errorMessage as string) ||
				(responseError?.message as string);
			dispatch(
				setToastMessage({
					message: errorMsg,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	const breadCrumbText = useSelector(
		(state: RootState) => state.common.requirementInventoryBreadcrumbText,
	);

	const renderNoRegulationItem = () => (
		<Grid item xs={12} sm={12} md={12} lg={12} xl={12} minHeight="35vw">
			<Typography className={styles['task-name']}>{group.groupName}</Typography>
			<Typography className={styles['no-content-text']}>
				No regulation associated to this group
			</Typography>
		</Grid>
	);

	return (
		<Grid container spacing={2}>
			{loading ? (
				<CircularProgress className={styles['progress-bar']} />
			) : (
				<>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12} pt={0}>
						<RegulationBreadcrumbs
							items={getGroupingBreadCrumbs(
								breadCrumbText,
								'',
								group?.groupName,
							)}
							storageWrapper={storageWrapper}
						/>
					</Grid>
					{group.taskId ? (
						<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
							<RegulationDetailsHeader
								taskId={group.taskId}
								status={group.taskStatus}
								assignedUserName={`${
									group.assignedUserName ? group.assignedUserName : ''
								}`}
								jurisdiction={group.jurisdiction || ''}
								reviewByDate={group.reviewByDate}
								title={group.groupName}
							/>
						</Grid>
					) : (
						renderNoRegulationItem()
					)}
					{group.taskId && group.grouping !== null ? (
						<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
							<Stack className={styles['citaion-deatils-wrapper']} gap="4px">
								{group?.grouping?.map((res) => (
									<AccordionComp
										className={styles['accordion-margin-btm']}
										expanded={selectedAccordians.includes(res)}
										onChange={(event) => handleAccordianChange(res, event)}
										isLeftAccordian
										dataTestId="accordian-comp"
										key={res.id}
										summary={
											<Stack
												direction="row"
												justifyContent="space-between"
												width="100%"
											>
												<Stack
													direction="row"
													alignItems="center"
													data-testid="accordion-title"
												>
													<Typography
														className={styles['inventory-citation-heading']}
													>
														{res.description}
													</Typography>
												</Stack>
											</Stack>
										}
										details={
											<div className={styles['inventory-accordian-detail']}>
												<CitationDescription
													currentNode={res}
													parentNode={res}
													isMultiCheckBoxRequired={false}
												/>
											</div>
										}
									/>
								))}
							</Stack>
						</Grid>
					) : (
						renderNoRegulationItem()
					)}
					<AlertComponent
						open={isToastOpen ?? false}
						toastMessage={message ?? ''}
						severity={severity ?? ''}
						handleCloseMessage={handleToast}
						vertical="top"
						horizontal="right"
					/>
					{group.taskStatus === GROUPING_REVIEW &&
						isAssignedUserSameAsLoggedInUser && (
							<Grid
								container
								spacing={2}
								justifyContent="flex-end"
								className={styles['inventory-submit-for-review-grid']}
							>
								<Grid item xs={12} sm={4} md={2} lg={2} xl={1}>
									<ButtonComp
										btnType="secondary"
										clickHandler={onGroupReviewReject}
									>
										Reject
									</ButtonComp>
								</Grid>
								<Grid item xs={12} sm={4} md={2} lg={2} xl={1}>
									<ButtonComp btnType="primary" onClick={onGroupReviewApproval}>
										Approve
									</ButtonComp>
								</Grid>
							</Grid>
						)}
				</>
			)}
		</Grid>
	);
};
export default GroupReviewDetails;
